import React, { useState } from 'react';
import { useAuth } from '@/hooks/auth';
import { useTranslation } from '@/hooks/useTranslation';
import { GenericPageHero } from '@/components/shared/GenericPageHero';
import { PageShell, SectionLoader } from '@/components/ui/progressive-loading';
import { SimplifiedRoleCard } from '@/components/role-management/SimplifiedRoleCard';
import { CompanyActionDialog } from '@/components/company/CompanyActionDialog';
import { Users, User, Building2, Target, Sparkles } from 'lucide-react';

export default function RoleManagementPage() {
  const [showCompanyDialog, setShowCompanyDialog] = useState(false);
  const { t } = useTranslation('navigation');
  const { t: tWorkspace } = useTranslation('workspace');
  const {
    isReady,
    hasTalentAccess,
    hasCompanyAccess,
    hasRecruiterAccess,
    hasInternalAccess,
    companies,
    selectedCompany
  } = useAuth();


  // Helper function to get company info
  const getCompanyInfo = () => {
    if (!hasCompanyAccess || !companies?.length) return undefined;
    
    if (companies.length === 1) {
      return `${tWorkspace('current_company')}: ${companies[0].name}`;
    } else {
      return `${companies.length} ${tWorkspace('companies_available')}`;
    }
  };

  const getCompanyDetails = () => {
    if (!hasCompanyAccess || !companies?.length || companies.length === 1) return undefined;
    
    return companies.map(company => company.name);
  };

  const roleData = [
    {
      icon: User,
      title: t('navigation:talent'),
      description: tWorkspace('talent_description'),
      hasAccess: hasTalentAccess,
      features: [
        tWorkspace('feature_job_search'),
        tWorkspace('feature_profile_management'),
        tWorkspace('feature_community'),
        tWorkspace('feature_calendar')
      ]
    },
    {
      icon: Building2,
      title: t('navigation:company'),
      description: tWorkspace('company_description'),
      hasAccess: hasCompanyAccess,
      roleInfo: getCompanyInfo(),
      companyDetails: getCompanyDetails(),
      alwaysShowAction: true,
      accessAction: {
        label: tWorkspace('join_create_company'),
        onClick: () => setShowCompanyDialog(true)
      },
      features: [
        tWorkspace('feature_job_posting'),
        tWorkspace('feature_candidate_management'),
        tWorkspace('feature_team_collaboration'),
        tWorkspace('feature_analytics')
      ]
    },
    {
      icon: Target,
      title: t('navigation:recruiter'),
      description: tWorkspace('recruiter_description'),
      hasAccess: hasRecruiterAccess,
      roleInfo: hasRecruiterAccess ? tWorkspace('role_professional_recruiter') : undefined,
      accessAction: hasRecruiterAccess ? undefined : {
        label: tWorkspace('become_recruiter'),
        path: "/apply/recruiter"
      },
      features: [
        tWorkspace('feature_candidate_sourcing'),
        tWorkspace('feature_commission_earnings'),
        tWorkspace('feature_advanced_search'),
        tWorkspace('feature_placement_tools')
      ]
    },
    ...(hasInternalAccess ? [{
      icon: Sparkles,
      title: tWorkspace('internal_admin_title'),
      description: tWorkspace('internal_admin_description'),
      hasAccess: hasInternalAccess,
      features: [
        tWorkspace('feature_user_management'),
        tWorkspace('feature_system_analytics'),
        tWorkspace('feature_platform_admin'),
        tWorkspace('feature_support_tools')
      ]
    }] : [])
  ];

  return (
    <PageShell>
      <GenericPageHero
        icon={Users}
        title={tWorkspace('role_management_title')}
        description={tWorkspace('role_management_description')}
      />

      {!isReady ? (
        <SectionLoader variant="card" count={3} showTitle />
      ) : (
        <div className="space-y-6">
          {roleData.map((role) => (
            <SimplifiedRoleCard key={role.title} {...role} />
          ))}
        </div>
      )}

      <CompanyActionDialog 
        isOpen={showCompanyDialog}
        onClose={() => setShowCompanyDialog(false)}
      />
    </PageShell>
  );
}